import { useRef, useState, DragEvent, ChangeEvent } from 'react';
import { 
  validateFile, 
  isFileExtensionOneOf, 
  getVideoDuration 
} from '@src/utils/files.utils';
import { MAX_FILES, SUPPORTED_EXTENSIONS, VIDEO_EXTENSIONS } from './setupSection.constants';

export interface UploadingFileWrapper {
  id: string;
  file: File;
  progress: number;
  remainingSeconds: number;
}

interface IUseSetupSectionProps {
  uploadedFiles: File[];
  onFilesChange: (files: File[]) => void;
  ctaDurationInSeconds: number;
}

export const useSetupSection = ({ 
  uploadedFiles, 
  onFilesChange, 
  ctaDurationInSeconds 
}: IUseSetupSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFileWrapper[]>([]);

  const handleFiles = async (files: FileList | null) => {
    setUploadError(null);
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    const validShapeFiles = newFiles.filter(f => validateFile(f, SUPPORTED_EXTENSIONS));

    if (validShapeFiles.length === 0) {
      setUploadError('No valid files selected. Please check supported formats.');
      return;
    }

    const validFiles: File[] = [];
    let hasDurationError = false;

    for (const file of validShapeFiles) {
      if (isFileExtensionOneOf(file.name, VIDEO_EXTENSIONS)) {
        try {
          const duration = await getVideoDuration(file);
          
          if (duration > ctaDurationInSeconds) {
            hasDurationError = true;
            continue;
          }
        } catch (error) {
          console.error('Error checking video duration:', error);
        }
      }
      validFiles.push(file);
    }

    if (hasDurationError) {
      setUploadError('The uploaded video duration is longer than the CTA duration. Please increase the CTA duration or use a shorter video.');
    }

    if (validFiles.length === 0) {
      return;
    }

    const currentTotalResponse = uploadedFiles.length + uploadingFiles.length;
    const remainingSlots = MAX_FILES - currentTotalResponse;
    
    if (remainingSlots <= 0) {
      setUploadError(`Maximum ${MAX_FILES} files allowed.`);
      return;
    }

    const filesToAdd = validFiles.slice(0, remainingSlots);
    
    if (filesToAdd.length < validFiles.length) {
      setUploadError(`Only ${filesToAdd.length} file(s) added. Maximum ${MAX_FILES} files allowed.`);
    }

    // Prepare wrapper objects
    const newUploadingWrappers: UploadingFileWrapper[] = filesToAdd.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      progress: 0,
      remainingSeconds: 2 // Start estimate
    }));

    setUploadingFiles(prev => [...prev, ...newUploadingWrappers]);
    setIsUploading(true);

    // Run parallel simulations
    await Promise.all(newUploadingWrappers.map(async (wrapper) => {
      const totalSteps = 20;
      // Randomize speed slightly for realism: 80ms to 150ms per step
      const stepDuration = 80 + Math.random() * 70; 

      for (let i = 1; i <= totalSteps; i++) {
        await new Promise(resolve => setTimeout(resolve, stepDuration));
        
        const progress = (i / totalSteps) * 100;
        const remainingMs = (totalSteps - i) * stepDuration;
        const remainingSeconds = Math.ceil(remainingMs / 1000);

        setUploadingFiles(prev => prev.map(item => 
          item.id === wrapper.id 
            ? { ...item, progress, remainingSeconds }
            : item
        ));
      }
    }));

    setUploadingFiles(prev => prev.filter(item => !newUploadingWrappers.find(w => w.id === item.id)));
    onFilesChange([...uploadedFiles, ...filesToAdd]);
    
    // Check if any left
    setUploadingFiles(prev => {
        if (prev.length === 0) setIsUploading(false);
        return prev;
    });
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    handleFiles(e.dataTransfer.files);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    if (index < uploadedFiles.length) {
        const newFiles = uploadedFiles.filter((_, i) => i !== index);
        onFilesChange(newFiles);
    }
  };

  return {
    fileInputRef,
    isDragging,
    isUploading,
    uploadError,
    uploadingFiles,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleBrowseClick,
    handleFileInputChange,
    handleRemoveFile
  };
};
